import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const DiscussionForums = ({ user }) => {
  const [forums, setForums] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedForum, setSelectedForum] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [newTopic, setNewTopic] = useState({ title: '', content: '', category_id: '', tags: '' });
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const arcaGreen = '#6B8E23';
  const arcaLightGreen = '#E8F5E9';

  // Fetch categories and topics
  useEffect(() => {
    fetchCategories();
    fetchTopics();
  }, [selectedForum]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/communication/forum/categories`);
      const data = await response.json();
      if (data.success && data.data.categories) {
        setForums(Array.isArray(data.data.categories) ? data.data.categories : []);
      } else {
        console.warn('No categories found in response:', data);
        setForums([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load forum categories');
    }
  };

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const url = selectedForum
        ? `${API_BASE}/communication/forum/topics?category_id=${selectedForum}`
        : `${API_BASE}/communication/forum/topics`;

      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        // Backend returns { topics: [], pagination: {} } inside data
        const topicsData = data.data.topics || data.data || [];
        setTopics(Array.isArray(topicsData) ? topicsData : []);
      } else {
        console.warn('Failed to fetch topics:', data.message);
        setTopics([]);
      }
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTopic = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.warning('Please login to create a topic');
      return;
    }

    if (!newTopic.title || !newTopic.content || !newTopic.category_id) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found. Please login again.');
        return;
      }

      const response = await fetch(`${API_BASE}/communication/forum/topics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newTopic,
          tags: newTopic.tags.split(',').map(t => t.trim()).filter(t => t)
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Topic created successfully!');
        setShowNewTopicModal(false);
        setNewTopic({ title: '', content: '', category_id: '', tags: '' });
        fetchTopics();
      } else {
        toast.error(data.message || 'Failed to create topic');
      }
    } catch (error) {
      console.error('Error creating topic:', error);
      toast.error('Failed to create topic');
    }
  };

  const handleViewTopic = (topicId) => {
    navigate(`/community/topic/${topicId}`);
  };

  // Filter topics based on search term
  const filteredTopics = topics.filter(topic => {
    const matchesSearch = topic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      topic.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      topic.author_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getColorClasses = (color) => {
    const colorMap = {
      blue: { bg: '#e3f2fd', text: '#1976d2', border: '#90caf9' },
      green: { bg: '#e8f5e9', text: '#388e3c', border: '#81c784' },
      purple: { bg: '#f3e5f5', text: '#7b1fa2', border: '#ba68c8' },
      yellow: { bg: '#fff8e1', text: '#f57f17', border: '#ffd54f' },
      red: { bg: '#ffebee', text: '#c62828', border: '#ef5350' },
      indigo: { bg: '#e8eaf6', text: '#3f51b5', border: '#7986cb' }
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <div>
      {/* Forum Categories */}
      <div className="row g-4 mb-4">
        {forums.map(forum => {
          const colors = getColorClasses(forum.color);
          return (
            <div key={forum.id} className="col-md-6 col-lg-4">
              <div
                className={`card border-0 shadow-sm h-100 ${selectedForum === forum.id ? 'border border-2' : ''
                  }`}
                style={{
                  cursor: 'pointer',
                  borderColor: selectedForum === forum.id ? arcaGreen : 'transparent',
                  transition: 'all 0.2s'
                }}
                onClick={() => setSelectedForum(selectedForum === forum.id ? null : forum.id)}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: colors.bg
                      }}
                    >
                      <i className="fas fa-comments" style={{ color: colors.text, fontSize: '24px' }}></i>
                    </div>
                    <h5 className="mb-0 ms-3 fw-bold">{forum.name}</h5>
                  </div>
                  <p className="text-muted small mb-3">{forum.description}</p>
                  <div className="d-flex justify-content-between text-muted small">
                    <span><i className="fas fa-file-alt me-1"></i>{forum.topic_count} topics</span>
                    <span><i className="fas fa-comments me-1"></i>{forum.post_count} posts</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Search and Actions */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="row align-items-center g-2">
            <div className="col-md-4 mb-2 mb-md-0">
              <h4 className="mb-0 fw-bold">Discussion Topics</h4>
            </div>
            <div className="col-md-8">
              <div className="d-flex gap-2 flex-wrap">
                <select
                  className="form-select"
                  style={{ maxWidth: '200px' }}
                  value={selectedForum || ''}
                  onChange={(e) => setSelectedForum(e.target.value || null)}
                >
                  <option value="">All Topics</option>
                  {forums.map(forum => (
                    <option key={forum.id} value={forum.id}>{forum.name}</option>
                  ))}
                </select>
                <div className="input-group flex-grow-1">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="fas fa-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    placeholder="Search topics..."
                    className="form-control border-start-0"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button
                  className="btn text-white fw-medium px-4"
                  style={{ backgroundColor: arcaGreen }}
                  onClick={() => {
                    if (!user) {
                      toast.warning('Please login to create a topic');
                      return;
                    }
                    setShowNewTopicModal(true);
                  }}
                >
                  <i className="fas fa-plus me-2"></i>New Topic
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Topics List */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ color: arcaGreen }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredTopics.length > 0 ? (
            <div className="list-group list-group-flush">
              {filteredTopics.map(topic => {
                const colors = getColorClasses(topic.category_color);
                return (
                  <div
                    key={topic.id}
                    className="list-group-item border-0 border-bottom py-4"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleViewTopic(topic.id)}
                  >
                    <div className="d-flex">
                      <img
                        src={topic.author_avatar ? `${process.env.REACT_APP_API_URL}${topic.author_avatar}` : `https://ui-avatars.com/api/?name=${encodeURIComponent(topic.author_name)}&background=6B8E23&color=fff`}
                        alt={topic.author_name}
                        className="rounded-circle me-3"
                        style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h5 className="mb-1 fw-bold">{topic.title}</h5>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: colors.bg,
                              color: colors.text
                            }}
                          >
                            {topic.category_name}
                          </span>
                        </div>
                        <p className="text-muted small mb-2">
                          <strong>{topic.author_name}</strong> • {new Date(topic.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-muted mb-3">
                          {topic.content.substring(0, 200)}{topic.content.length > 200 ? '...' : ''}
                        </p>
                        <div className="d-flex gap-3 text-muted small">
                          <span><i className="fas fa-eye me-1"></i>{topic.view_count} views</span>
                          <span><i className="fas fa-comments me-1"></i>{topic.reply_count} replies</span>
                          <span><i className="fas fa-heart me-1"></i>{topic.like_count} likes</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-comments fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No topics found</h5>
              <p className="text-muted">Try adjusting your search or create a new topic</p>
            </div>
          )}
        </div>
      </div>

      {/* New Topic Modal */}
      {showNewTopicModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header" style={{ backgroundColor: arcaGreen }}>
                <h5 className="modal-title text-white fw-bold">
                  <i className="fas fa-plus-circle me-2"></i>Create New Topic
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowNewTopicModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleCreateTopic}>
                  <div className="mb-3">
                    <label className="form-label fw-medium">Category *</label>
                    <select
                      className="form-select"
                      value={newTopic.category_id}
                      onChange={(e) => setNewTopic({ ...newTopic, category_id: e.target.value })}
                      required
                    >
                      <option value="">Select a category...</option>
                      {forums.map(forum => (
                        <option key={forum.id} value={forum.id}>{forum.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-medium">Title *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter topic title..."
                      value={newTopic.title}
                      onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })}
                      required
                      maxLength={300}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-medium">Content *</label>
                    <textarea
                      className="form-control"
                      rows="6"
                      placeholder="Share your thoughts, questions, or experiences..."
                      value={newTopic.content}
                      onChange={(e) => setNewTopic({ ...newTopic, content: e.target.value })}
                      required
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-medium">Tags (optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., ventilator, COPD, pediatrics (comma-separated)"
                      value={newTopic.tags}
                      onChange={(e) => setNewTopic({ ...newTopic, tags: e.target.value })}
                    />
                    <small className="text-muted">Separate tags with commas</small>
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary px-4"
                      onClick={() => setShowNewTopicModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white px-4 fw-medium"
                      style={{ backgroundColor: arcaGreen }}
                    >
                      <i className="fas fa-paper-plane me-2"></i>Create Topic
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscussionForums;